import { memo } from "react";

import type { BoardViewMode } from "@/features/player-kanban/types/types";

import { Pressable } from "@/components/atoms/Pressable";
import { Stack } from "@/components/atoms/Stack";
import { Text } from "@/components/atoms/Text";
import { TEST_ID_PLAYER_KANBAN } from "e2e/testId";

/** Shared segment box — Figma Toggle-item is 28px tall with 8px side padding. */
const SEGMENT_BASE = "h-7 flex-row items-center justify-center rounded-sm px-2";

/**
 * Resolve the segment container classes.
 * @param isSelected - Whether this segment's renderer is mounted
 * @returns The segment's className
 */
const segmentClass = (isSelected: boolean) =>
  `${SEGMENT_BASE} ${isSelected ? "bg-surface-base" : "bg-transparent"}`;

/**
 * Resolve the segment label classes.
 * @remarks
 * Both labels are 16px (`title-sm`). The design sets the selected label Bold
 * (Heading/S, carried by the variant) and the unselected one Regular
 * (Text/Base), which the closed type scale has no 16px-regular bundle for —
 * `font-body` overrides the variant's weight, matching `SandboxBannerView`.
 * @param isSelected - Whether this segment's renderer is mounted
 * @returns The label's className
 */
const segmentToneClass = (isSelected: boolean) =>
  isSelected ? "text-content-primary" : "font-body text-content-muted";

/**
 * Props for BoardViewToggleView
 */
interface BoardViewToggleViewProps {
  /** The renderer currently mounted on the board */
  readonly value: BoardViewMode;
  /** Select the Kanban renderer */
  readonly onSelectKanban: () => void;
  /** Select the Table renderer */
  readonly onSelectTable: () => void;
}

/**
 * Segmented Kanban ⇄ Table switch rendered in the board action bar (SE-5491).
 * @remarks
 * Placement mirrors the Figma Action Bar (node 80:14988) — board chrome beside
 * Filter / Share / Tags, NOT the sidebar's SearchAndControls. The design is
 * label-only: a `neutral/200` track holding a white pill on the active segment,
 * with no leading icons (SE-6554 re-baseline divergence #9).
 * @param props - Component props
 * @param props.value - The renderer currently mounted on the board
 * @param props.onSelectKanban - Select the Kanban renderer
 * @param props.onSelectTable - Select the Table renderer
 */
const BoardViewToggleView = ({
  value,
  onSelectKanban,
  onSelectTable,
}: BoardViewToggleViewProps) => (
  <Stack
    direction="horizontal"
    testID={TEST_ID_PLAYER_KANBAN.VIEW_TOGGLE}
    className="items-center gap-1 rounded-sm bg-surface-subtle p-micro"
  >
    <Pressable
      testID={TEST_ID_PLAYER_KANBAN.VIEW_TOGGLE_KANBAN}
      accessibilityRole="button"
      accessibilityLabel="Show board as kanban"
      accessibilityState={{ selected: value === "kanban" }}
      onPress={onSelectKanban}
      className={segmentClass(value === "kanban")}
    >
      <Text variant="title-sm" className={segmentToneClass(value === "kanban")}>
        Kanban
      </Text>
    </Pressable>

    <Pressable
      testID={TEST_ID_PLAYER_KANBAN.VIEW_TOGGLE_TABLE}
      accessibilityRole="button"
      accessibilityLabel="Show board as table"
      accessibilityState={{ selected: value === "table" }}
      onPress={onSelectTable}
      className={segmentClass(value === "table")}
    >
      <Text variant="title-sm" className={segmentToneClass(value === "table")}>
        Table
      </Text>
    </Pressable>
  </Stack>
);

BoardViewToggleView.displayName = "BoardViewToggleView";

export default memo(BoardViewToggleView);
